import type React from 'react';

import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

import AvoidanceDialogActions from 'components/dialogs/avoidance/AvoidanceDialogActions';
import { useStyles } from 'components/dialogs/avoidance/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import AvoidanceRulesPanel from 'components/panels/avoidance/AvoidanceRulesPanel';
import { BodyRegular, H3 } from 'components/typography/Typography';
import { tournamentEngineMappedConstants } from 'constants/avoidance';
import { type PlainAvoidanceRuleInterface } from 'typedefs/avoidance';

interface AvoidanceRulesProps {
  avoidanceRules: PlainAvoidanceRuleInterface[];
  isOpen?: boolean;
  onClose: () => void;
  setAvoidance: (avoidance) => void; // TODO: add avoidance type
  setAvoidanceRules: (avoidanceRules: PlainAvoidanceRuleInterface[]) => void;
  setChosenAvoidanceRules: (avoidanceRules: PlainAvoidanceRuleInterface[]) => void;
}

const AvoidanceRulesDialog: React.FC<React.PropsWithChildren<AvoidanceRulesProps>> = ({
  avoidanceRules,
  isOpen,
  onClose,
  setAvoidance,
  setAvoidanceRules,
  setChosenAvoidanceRules,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const paperProps = { style: { padding: 30 } };
  const checkedRules = avoidanceRules?.filter((rule) => rule.checked);

  const handleSetAvoidanceRules = (avoidanceRule) => {
    const newAvoidanceRules = avoidanceRules.map((rule) => {
      if (rule.index === avoidanceRule.index) {
        return {
          ...rule,
          checked: !rule.checked,
        };
      }
      return rule;
    });
    setAvoidanceRules(newAvoidanceRules);
  };
  const clearAvoidanceRules = () => {
    const newAvoidanceRules = avoidanceRules.map((rule) => ({
      ...rule,
      checked: false,
    }));
    setAvoidanceRules(newAvoidanceRules);
  };
  const chooseAvoidance = () => {
    const tournamentEngineCompliantRules = checkedRules.flatMap((rule) => tournamentEngineMappedConstants[rule.name]);
    const avoidance = {
      roundsToSeparate: 1,
      policyAttributes: tournamentEngineCompliantRules,
    };
    setChosenAvoidanceRules(avoidanceRules);
    setAvoidance(avoidance);
    onClose();
  };

  const closeModal = () => {
    clearAvoidanceRules();
    onClose();
  };

  return (
    <TMXDialog fullWidth open={isOpen} PaperProps={paperProps} onClose={onClose}>
      <Grid container direction="column">
        <Grid item>
          <H3>{t('avoidanceRulesDialog.header')}</H3>
        </Grid>
        <Grid item>
          <BodyRegular>{t('avoidanceRulesDialog.bodyRegular')}</BodyRegular>
        </Grid>
        <AvoidanceRulesPanel avoidanceRules={avoidanceRules} setAvoidanceRules={handleSetAvoidanceRules} />
        <Grid item className={classes.actionsWrapper}>
          <AvoidanceDialogActions
            cancelAction={closeModal}
            chooseAvoidance={chooseAvoidance}
            clearAvoidanceRules={clearAvoidanceRules}
            numberOfSelected={checkedRules?.length || 0}
          />
        </Grid>
      </Grid>
    </TMXDialog>
  );
};

export default AvoidanceRulesDialog;
